// SOS alerts on the live map: banners for members who need help, the
// caller's own open alert, and what an "I'm on it"/"I'm safe" request came
// to. Pure, so it is unit-tested (Android: sos/Sos.kt).
import type { Schemas } from "@10twenty/api";
import type { MemberRow } from "./liveCircle";

export interface SosBanner {
  userId: string;
  alertId: string;
  name: string;
  /** When the alert was opened (epoch ms). */
  sinceMs: number;
  /** Other members who acknowledged it, earliest first. */
  responders: string[];
  ackedBySelf: boolean;
  /** The member's latest fix, else the one sent with the alert. */
  location: Schemas["LiveLocation"] | null;
}

/** Other members' open alerts, oldest first. */
export function sosBanners(rows: readonly MemberRow[]): SosBanner[] {
  const self = rows.find((row) => row.isSelf)?.userId;
  const names = new Map(rows.map((row) => [row.userId, row.name]));
  return rows
    .flatMap((row) => (row.isSelf || !row.sos ? [] : [{ row, sos: row.sos }]))
    .map(({ row, sos }) => ({
      userId: row.userId,
      alertId: sos.id,
      name: row.name,
      sinceMs: Date.parse(sos.since),
      responders: sos.acked_by.flatMap((ack) => {
        const name = ack.user_id === self ? undefined : names.get(ack.user_id);
        return name === undefined ? [] : [name];
      }),
      ackedBySelf: sos.acked_by.some((ack) => ack.user_id === self),
      location: row.location ?? sos.location,
    }))
    .sort((a, b) => a.sinceMs - b.sinceMs);
}

/** The caller's own open alert, if the circle shows one. */
export function ownSos(rows: readonly MemberRow[]): { alertId: string; sinceMs: number; responders: string[] } | null {
  const self = rows.find((row) => row.isSelf);
  if (!self?.sos) return null;
  const names = new Map(rows.map((row) => [row.userId, row.name]));
  return {
    alertId: self.sos.id,
    sinceMs: Date.parse(self.sos.since),
    responders: self.sos.acked_by.flatMap((ack) => {
      const name = names.get(ack.user_id);
      return name === undefined || ack.user_id === self.userId ? [] : [name];
    }),
  };
}

/** "Bob is responding", "Bob and Carol are responding", "Bob, Carol and Dan are responding". */
export function respondersText(responders: readonly string[], ackedBySelf = false): string | null {
  const names = ackedBySelf ? ["You", ...responders] : [...responders];
  if (names.length === 0) return null;
  const verb = names.length === 1 && !ackedBySelf ? "is" : "are";
  const list = names.length === 1 ? names[0]! : `${names.slice(0, -1).join(", ")} and ${names[names.length - 1]!}`;
  return `${list} ${verb} responding`;
}

/** Directions to the member in the phone's or browser's maps app. */
export function directionsUrl(location: { lat: number; lon: number }): string {
  return `https://www.google.com/maps/dir/?api=1&destination=${location.lat.toFixed(6)},${location.lon.toFixed(6)}`;
}

export type SosActionOutcome =
  | { kind: "done" }
  /** The alert closed meanwhile (409 `sos_resolved`). */
  | { kind: "resolved" }
  /** The member or the alert is gone, or the caller lost access. */
  | { kind: "gone" }
  | { kind: "error"; message: string };

/** Classifies the response to `ackSos` or `resolveSos`. */
export function sosActionOutcome(status: number): SosActionOutcome {
  if (status >= 200 && status < 300) return { kind: "done" };
  if (status === 409) return { kind: "resolved" };
  if (status === 403 || status === 404) return { kind: "gone" };
  return { kind: "error", message: `Request failed (HTTP ${status})` };
}

/**
 * A short message about a failed action on `name`'s alert (null: the caller's
 * own), or null when there is nothing to say.
 */
export function sosActionText(outcome: SosActionOutcome, name: string | null): string | null {
  const whose = name === null ? "Your" : `${name}'s`;
  switch (outcome.kind) {
    case "done":
      return null;
    case "resolved":
      return `${whose} SOS has already ended.`;
    case "gone":
      return `${whose} SOS is no longer available.`;
    case "error":
      return outcome.message;
  }
}
